import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useStats } from "@/context/StatsContext";
import CampsiteMap from "@/components/dashboard/CampsiteMap";
import KpiBar from "@/components/dashboard/KpiBar";
import PlanningGrid from "@/components/dashboard/PlanningGrid";
import ShiftNotes from "@/components/dashboard/ShiftNotes";

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const { stats, reload } = useStats();
  const [pitches, setPitches] = useState([]);
  const [guests, setGuests] = useState([]);
  const [view, setView] = useState(null);
  const [handoverLabels, setHandoverLabels] = useState([]);

  // De voorkeur van de gebruiker komt pas binnen na /auth/me; lokaal bewaard is ze er meteen
  useEffect(() => {
    if (view !== null) return;
    const saved = localStorage.getItem("campad.view");
    if (saved === "map" || saved === "planning") { setView(saved); return; }
    if (user) setView(user.view_mode === "planning" ? "planning" : "map");
  }, [user, view]);

  const load = useCallback(async () => {
    try {
      const [p, g] = await Promise.all([api.get("/pitches"), api.get("/guests"), reload()]);
      setPitches(p.data);
      setGuests(g.data);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const moves = (await api.get("/moves?status=pending")).data || [];
        const labels = [];
        moves.filter((m) => m.date === today).forEach((m) => { if (m.from_pitch) labels.push(m.from_pitch); if (m.to_pitch) labels.push(m.to_pitch); });
        setHandoverLabels([...new Set(labels)]);
      } catch (_) { setHandoverLabels([]); }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, [reload]);

  // De kaart heeft plekken, gasten en statistieken nodig; de planning haalt zijn eigen data op.
  useEffect(() => { if (view === "map") load(); }, [view, load]);

  const switchView = async (next) => {
    setView(next);
    localStorage.setItem("campad.view", next);
    try { await api.put("/me/view-mode", { view_mode: next }); } catch (_) { /* voorkeur niet kunnen bewaren is niet blokkerend */ }
  };

  const updatePitch = async (id, body) => {
    try {
      await api.patch(`/pitches/${id}`, body);
      await load();
      toast.success(t("saved"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (view === null) return <div className="h-72" data-testid="dashboard" />;

  if (view === "planning") {
    return (
      <div data-testid="dashboard">
        <KpiBar />
        <ShiftNotes />
        <PlanningGrid onSwitchToMap={() => switchView("map")} />
      </div>
    );
  }

  return (
    <div data-testid="dashboard">
      <KpiBar />
      <ShiftNotes />
      <div className="bg-card rounded-xl border border-border/70 shadow-sm overflow-hidden">
        <CampsiteMap
          pitches={pitches}
          stats={stats}
          guests={guests}
          onUpdate={updatePitch}
          onReload={load}
          highlightLabels={[]}
          handoverLabels={handoverLabels}
          onSwitchToPlanning={() => switchView("planning")}
        />
      </div>
    </div>
  );
}
